import type { FloatAlign } from "@ext/markdown/elements/float/edit/model/types";
import {
	type AnnotationObject,
	type Crop,
	type ImageObject,
	ImageObjectTypes,
	type SquareObject,
} from "@ext/markdown/elements/image/edit/model/imageEditorTypes";

export const parse = (
	crop: string,
	scale: string,
	objects: string,
	width: number,
	height: number,
	float: FloatAlign,
): {
	crop: Crop;
	objects: ImageObject[];
	scale?: number | string;
	width: number;
	height: number;
	float: FloatAlign;
} => {
	const scaleIsObjects = isObjects(scale);
	const newCrop = transfromToCrop(crop);
	const newObjects = transformToObjects((scaleIsObjects ? scale : objects) ?? "[]");
	const newFloat = float || "center";

	const parsedScale = !scaleIsObjects && scale !== null ? (scale?.endsWith("px") ? scale : +scale) : null;

	return {
		crop: newCrop,
		objects: newObjects,
		scale: parsedScale ?? null,
		width: width,
		height: height,
		float: newFloat,
	};
};

const isObjects = (objects: string): boolean => {
	return typeof objects === "string" && objects?.length > 3 && !objects.endsWith("px");
};

const transfromToCrop = (crop: string): Crop => {
	const [x, y, w, h] = crop
		.split(",")
		.map((i) => +i)
		.values();
	return { x: x, y: y, w: w, h: h };
};

const transformToObjects = (objects: string): ImageObject[] => {
	const newObjects: ImageObject[] = [];
	if (objects.length === 2 || objects.length === 0) return newObjects;

	objects.split("&").forEach((object) => {
		const data = object.split(",");
		const type: ImageObjectTypes = data[0] as ImageObjectTypes;
		let newObject: ImageObject;

		switch (type) {
			case ImageObjectTypes.Annotation:
				newObject = {
					type: ImageObjectTypes.Annotation,
					x: +data[1],
					y: +data[2],
					text: data[3],
					direction: data[4],
				} as AnnotationObject;
				break;
			case ImageObjectTypes.Square:
				newObject = {
					type: ImageObjectTypes.Square,
					x: +data[1],
					y: +data[2],
					w: +data[3],
					h: +data[4],
					text: data[5],
					direction: data[6],
				} as SquareObject;
				break;
			default:
				console.warn(`TransformToObjects: Unknown object type, objects: ${objects}`);
		}

		newObjects.push(newObject);
	});

	return newObjects;
};

export const isFullFrame = (crop: Crop) => !crop || (crop.x === 0 && crop.y === 0 && crop.w === 100 && crop.h === 100);

/** A crop or a drawing on top, made in the image editor. */
export const hasEditorChanges = (crop: Crop | string, objects: ImageObject[]) =>
	(typeof crop === "object" && !isFullFrame(crop)) || objects?.length > 0;

export const DEFAULT_FLOAT: FloatAlign = "center";

export const format = (
	width: number,
	height: number,
	crop: Crop,
	objects: ImageObject[],
	scale?: number,
	float?: FloatAlign,
) => {
	// biome-ignore lint/suspicious/noExplicitAny: it's ok
	const result: Record<string, any> = {};

	// Read back, `crop` and `float` at their defaults count as set, and the image never returns to the short form.
	result.crop = isFullFrame(crop) ? "" : Object.values(crop).join(",");
	result.scale = scale ? scale.toString() : "";
	result.objects = "";

	if (Array.isArray(objects) && objects.length > 0) {
		result.objects = objects
			.filter((data) => data?.type)
			.map((data, index) => (index !== 0 ? "&" : "") + Object.values(data).join(","))
			.join("");
	}

	// Empty rather than absent: Legacy tags are positional, and every slot after the size has to keep its place.
	result.width = width && height ? width : "";
	result.height = width && height ? height : "";

	result.float = float && float !== DEFAULT_FLOAT ? float : "";

	return result;
};
